<template>
  <div
    class="list-row-container"
    data-test="topic-list-row"
  >
    <button
      type="button"
      class="row-content button-reset"
      @click="rowClick"
    >
      <template v-if="thumbnail">
        <div
          v-if="thumbnail"
          class="thumbnail thumbnail-img"
          role="img"
          :aria-label="`Media for ${name}`"
          :style="{ backgroundImage: thumbnail }"
        />
        <div
          v-else
          class="thumbnail"
        />
      </template>
      <div
        v-if="hasSlot"
        class="name"
      >
        <slot name="name" />
      </div>
      <div
        v-else
        class="name"
        data-test="topic-row-name"
        v-html="name"
      />
      <purchase-order-preview
        v-if="user.previewer"
        :resource="rowTopic"
      />
      <lock-button
        v-if="locked"
        :resource="{ type: 'topic', id: rowTopic.id }"
      />
    </button>
    <template v-if="showBookmarkIcon">
      <button
        type="button"
        class="button-reset"
        :aria-label="isBookmarked ? 'Remove bookmark' : 'Add bookmark'"
        :aria-pressed="isBookmarked"
        @click.stop="toggleBookmark"
        @keydown.enter.stop.prevent="toggleBookmark"
        @keydown.space.stop.prevent="toggleBookmark"
      >
        <icon
          :name="isBookmarked ? 'bookmark-icon-filled' : 'bookmark-icon'"
          class="bookmark-icon"
        />
      </button>
    </template>
    <template v-if="!hideAction">
      <div
        v-if="showLastViewed"
        class="last-viewed"
      >
        <span>{{ lastViewed }}</span>
      </div>
      <div
        v-else
        class="action-container"
      >
        <icon
          name="arrow"
          class="arrow"
          variant="4fb9de"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { format } from 'date-fns';

import LockButton from '@components/singletons/lock-button';
import Icon from '@components/singletons/icon';
import PurchaseOrderPreview from '@components/singletons/purchaseOrderPreview';

export default {
  name: 'TopicListRow',
  components: { Icon, LockButton, PurchaseOrderPreview },
  props: {
    card: {
      type: Object,
    },
    hideAction: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    topic: {
      type: Object,
    },
    showBookmarkIcon: {
      type: Boolean,
      default: false,
    },
    isBookmarked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['row-clicked', 'toggle-bookmark'],
  computed: {
    bookmarkSource() {
      if (this.card) {
        return { type: 'Card', id: this.card.id };
      } else {
        return this.source;
      }
    },
    name() {
      if (this.card?.title) {
        return this.card.title;
      }
      return this.rowTopic?.assetTitle;
    },
    hasSlot() {
      return !!this.$slots.name;
    },
    lastViewed() {
      const state = this.$store.getters['topicStates/topicStates'][this.rowTopic?.id];
      if (state) {
        return format(new Date(state.mobileCreatedAt), 'MMM d, yyyy h:mm a');
      } else {
        return '';
      }
    },
    rowTopic() {
      if (this.card) {
        return this.$store.getters['topics/topics'][this.card.assetId];
      } else if (this.topic) {
        return this.topic;
      }
    },
    showLastViewed() {
      if (this.card) {
        return false;
      }

      return true;
    },
    thumbnail() {
      if (this.rowTopic) {
        const attachment = this.$store.getters['attachments/get'][this.rowTopic.displayAttachmentId];
        if (attachment) {
          return `url("${attachment.smallThumbnail}")`;
        }
      }
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    rowClick() {
      const param = this.card ? this.card : this.rowTopic;
      this.$emit('row-clicked', param);
    },
    toggleBookmark() {
      this.$emit('toggle-bookmark');
    },
  },
};
</script>

<style lang="scss" scoped>
.row-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: inherit;
  background-color: inherit;
  font-weight: $semi-bold;

  .name {
    cursor: pointer;
    align-items: center;
    display: flex;
    font-size: rem-calc(14);
    text-align: left;
    min-height: rem-calc(24);
    flex: 1;
    color: var(--text-primary-900);
    font-weight: var(--Fontweightfont-weight-medium);
  }

  .thumbnail {
    align-items: center;
    background-color: #ebedef;
    display: flex;
    flex-shrink: 0;
    height: 39px;
    justify-content: center;
    margin-left: rem-calc(24);
    width: 70px;

    &.thumbnail-img {
      background-position: center;
      background-size: cover;
    }
  }
}

.bookmark-icon {
  height: 24px;
}

.last-viewed {
  color: #7f8fa4;
  font-size: rem-calc(10);
  margin-left: auto;
  width: rem-calc(200);
  text-align: right;

  @include breakpoint(small) {
    display: none;
  }
}

.action-container {
  display: flex;
  align-items: center;
  flex-direction: row;
  margin-left: auto;
  min-width: rem-calc(60);

  @include breakpoint(small) {
    flex-direction: column-reverse;
    min-width: rem-calc(40);
  }

  .arrow {
    margin: auto 0 auto rem-calc(24);
    width: rem-calc(14);

    @include breakpoint(small) {
      margin-left: 0;
    }
  }
}
</style>
